import React from "react";
import { Grid, Typography, Box, Divider } from "@mui/material";
import ScenarioTable from "./ScenarioTable";
import {
  BCPHandshake,
  BCPTimer,
  BCPFlag
} from "../../../components/icons/bcp/BCPIcons";
import { FaSwords } from "../../../components/icons/fa/FaIcon";
import AttackDefendStats from "./AttackDefendStats";

const WIN_CONDITION_RENDER_MAP = {
  Assassination: (color) => (
    <FaSwords sx={{ "& path": { fill: color }, fontSize: "20px" }} />
  ),
  Concession: (color) => <BCPHandshake size={20} style={{ color }} />,
  Deadlock: (color) => <BCPTimer size={20} style={{ color }} />,
  Scenario: (color) => <BCPFlag size={20} style={{ color }} />
};

export default function ScenariosStats({ data, theme }) {
  if (!data) return null;

  const isDark = theme.palette.mode === "dark";
  const { gameSystem, missions, winConditions, firstSecond, pointSize } = data;

  const sectionTitle = gameSystem?.missionLabelPlural || "Missions";
  const missionLabel = gameSystem?.missionLabel || "Mission";
  const firstSecondLabel =
    gameSystem?.firstSecondLabel || "Attacker / Defender";
  const pointSizeLabel = gameSystem?.pointSizeLabel || "Point Size";

  const missionItems =
    missions?.map((m) => ({ label: m.mission, games: m.games })) || [];
  const winConditionItems =
    winConditions?.map((w) => ({ label: w.winCondition, games: w.games })) ||
    [];
  const pointSizeItems =
    pointSize?.map((p) => ({ label: p.label, games: p.games })) || [];

  const winConditionTotal = winConditionItems.reduce(
    (sum, item) => sum + item.games,
    0
  );

  const hasMissions = missionItems.length > 0;
  const hasWinConditions = winConditionItems.length > 0;
  const hasFirstSecond = firstSecond?.length > 0;
  const hasPointSize = pointSizeItems.length > 0;

  const visibleCount = [hasMissions, hasWinConditions, hasFirstSecond, hasPointSize].filter(Boolean).length;

  if (visibleCount === 0) return null;

  let visibleIdx = 0;
  const missionVisibleIdx = hasMissions ? visibleIdx++ : null;
  const winConditionVisibleIdx = hasWinConditions ? visibleIdx++ : null;
  const firstSecondVisibleIdx = hasFirstSecond ? visibleIdx++ : null;
  const pointSizeVisibleIdx = hasPointSize ? visibleIdx++ : null;

  const getItemColSize = (itemIdx) => {
    if (visibleCount === 1) return 12;
    if (visibleCount % 2 !== 0 && itemIdx === visibleCount - 1) return 12;
    return 6;
  };

  return (
    <Box sx={{ mt: 6 }}>
      <Divider
        sx={{
          mb: 4,
          borderColor: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"
        }}
      />

      <Typography
        variant="h5"
        sx={{
          mb: 3,
          color: theme.palette.text.primary,
          fontWeight: 600,
          textAlign: "center",
          letterSpacing: "0.05em"
        }}
      >
        {sectionTitle}
      </Typography>

      <Grid container spacing={3}>
        {hasMissions && (
          <Grid item xs={12} md={getItemColSize(missionVisibleIdx)}>
            <ScenarioTable
              title={missionLabel}
              items={missionItems}
              theme={theme}
              showAnalyze
            />
          </Grid>
        )}

        {hasWinConditions && (
          <Grid item xs={12} md={getItemColSize(winConditionVisibleIdx)}>
            <ScenarioTable
              title="Win Condition"
              items={winConditionItems}
              theme={theme}
              iconRenderMap={WIN_CONDITION_RENDER_MAP}
              footerNote={`/ ${winConditionTotal.toLocaleString()} Games`}
            />
          </Grid>
        )}

        {hasFirstSecond && (
          <Grid item xs={12} md={getItemColSize(firstSecondVisibleIdx)}>
            <AttackDefendStats
              rows={firstSecond}
              label={firstSecondLabel || "Attacker / Defender"}
              theme={theme}
            />
          </Grid>
        )}

        {hasPointSize && (
          <Grid item xs={12} md={getItemColSize(pointSizeVisibleIdx)}>
            <ScenarioTable
              title={pointSizeLabel}
              items={pointSizeItems}
              theme={theme}
              twoColumn
              highlightDominant
            />
          </Grid>
        )}
      </Grid>
    </Box>
  );
}
